/**
 * AssetGenJobsRepo — typed repository over {@link DocStorePort} for
 * `assetGenJobs/{jobId}`, the ASYNC asset-studio generation queue. Structurally
 * a sibling of {@link BuildJobsRepo}: the kickoff callable enqueues a `queued`
 * job, a builder worker CLAIMS it (race-free `queued → running` compare-and-set
 * via {@link DocStorePort.guardedUpdate}), heartbeats a coarse `stage` while the
 * python CLI runs, and marks it `succeeded` (with `variantCount`) or `failed`.
 *
 * Two deliberate differences from build jobs:
 *  - The row carries the four attribution fields captured at kickoff, so the
 *    builder-side completion bookkeeping credits the attributed member across
 *    the async boundary (a claim/requeue never re-derives attribution).
 *  - Kickoff and automatic completion coordinate under the creation lock;
 *    overlapping animation states are deduplicated even when their job names
 *    differ. {@link findOutstanding} remains a read-only progress lookup.
 */
import {
  PlatformError,
  type DocStorePort,
  type DocTxOps,
  type DocTxRunner,
  type QuerySpec,
  type ServerTimestampSentinel,
} from '@djgg/core';
import { canonicalJson } from '@djgg/gameplay-kit';
import { assetGenJobDoc, assetGenJobsCollection, assetVariantDoc, buildJobDoc, compositionRuntimeControlDoc, creationDoc, versionDoc } from '../paths.js';
import { validateCompositionRuntimeControlV2 } from '../compositionControls.js';
import { compositionMediaCallPlanV1, compositionMediaDeadlineAt, compositionMediaExecutionDigest, compositionMediaRouteV1,
  validateAcceptedCompositionV1, validateCompositionDeferredMediaV1, type CompositionDeferredMediaV1,
  type CompositionMediaExecutionV1 } from '../spec/gameSpec.js';
import { audioBriefRefusalDetail } from '../audioGeneration.js';
import { compositionMediaParentOpen, validateCompositionRoute } from './compositionRoute.js';
import { COMPOSITION_MUSIC_SLOT, compositionDerivedSlotsV1, compositionMediaSlotKindV1, isCompositionCoverStandInV1 } from '../spec/compositionPresentation.js';
import { isAnimStateName, stripAssetName } from '../animation.js';
import type {
  AssetGenJob,
  AssetGenStage,
  AssetVariantDoc,
  BuildJob,
  Creation,
  CreationAsset,
  CreationAssetKind,
  CreationVersion,
  MeasuredBuildOperationKind,
  MeasuredBuildReservationV1,
} from '../types.js';
import { AssetRejectionsRepo } from './assetRejections.js';
import { compositionMediaResumeJournal, isReusableCompositionJournalRowV1, MAX_VISUAL_RESUME_CHAIN } from './compositionMediaResume.js';

export { MAX_VISUAL_RESUME_CHAIN } from './compositionMediaResume.js';

interface FundedCompositionMediaContext {
  accepted: Awaited<ReturnType<typeof validateAcceptedCompositionV1>>;
  execution: CompositionMediaExecutionV1;
  deadlineAt: number;
}

/** Shared funded-child authority used both immediately before provider
 * reservation and at the atomic staged-variant publication boundary. All rows
 * are already locked by the caller in the global control -> parent -> creation
 * -> version -> child order (the reservation path has no creation mutation). */
async function validateFundedCompositionMediaContext(input: {
  controlStored: Record<string, unknown> | null;
  parent: BuildJob | null;
  version: CreationVersion | null;
  job: AssetGenJob | null;
  jobId: string;
  workerId: string;
  expectedBuildJobId: string;
  expectedRole: 'composition-media@2';
  now: number;
}): Promise<FundedCompositionMediaContext> {
  const { controlStored, parent, version, job } = input;
  const authority = job?.measuredBudget;
  const run = parent?.measuredExecution;
  if (!controlStored || !parent || !version || !job || !authority || !run
    || authority.role !== input.expectedRole || !authority.authorityDigest || !authority.executionDigest
    || authority.buildJobId !== input.expectedBuildJobId
    || authority.acceptedAt !== run.acceptedAt || authority.deadlineAt !== run.deadlineAt
    || job.status !== 'running' || job.workerId !== input.workerId) {
    throw new PlatformError('unavailable', 'Composition media execution authority is unavailable', { retryable: false });
  }
  let control;
  try {
    const { id: _id, ...controlBody } = controlStored;
    control = validateCompositionRuntimeControlV2(controlBody);
  } catch {
    throw new PlatformError('unavailable', 'Composition media execution control is invalid', { retryable: false });
  }
  const accepted = await validateAcceptedCompositionV1(version.executionPolicy?.composition);
  await validateCompositionRoute(parent, version, input.expectedBuildJobId);
  const execution = accepted.media?.execution;
  const executionDigest = execution ? await compositionMediaExecutionDigest(execution) : undefined;
  const barrier = run.compositionBarrier;
  const prerequisite = job.prerequisite;
  const deadlineAt = execution && prerequisite?.kind === 'composition-proof' && prerequisite.releasedAt
    ? compositionMediaDeadlineAt({ execution, parentDeadlineAt: run.deadlineAt,
        finishReserveMs: run.limits.finishReserveMs, releasedAt: prerequisite.releasedAt })
    : 0;
  if (!execution || !accepted.runtimeReleaseId || accepted.digest !== authority.authorityDigest
    || run.compositionDigest !== accepted.digest || run.compositionMediaExecutionDigest !== executionDigest
    || authority.executionDigest !== executionDigest
    || job.creationId !== parent.creationId || job.creationRun?.version !== parent.version
    || job.creationRun?.branch !== 'visual' || job.kind !== 'background' || job.name !== 'creation-plan'
    || canonicalJson([...(job.plan?.assetNames ?? [])].sort()) !== canonicalJson([...accepted.media!.slots].sort())
    || version.creationRun?.codeJobId !== input.expectedBuildJobId || version.creationRun?.visualJobId !== input.jobId
    || !['generating', 'gating'].includes(version.buildStatus)
    || !compositionMediaParentOpen(parent, input.now)
    || !barrier || prerequisite?.kind !== 'composition-proof'
    || barrier.authorityDigest !== accepted.digest || canonicalJson(barrier.mediaJobIds) !== canonicalJson([input.jobId])
    || prerequisite.authorityDigest !== accepted.digest || prerequisite.mediaBasisDigest !== barrier.mediaBasisDigest
    || prerequisite.releasedAt !== barrier.committedAt || input.now >= deadlineAt
    || control.executionRevocations.releaseIds.includes(accepted.runtimeReleaseId)
    || control.executionRevocations.closureDigests.includes(accepted.closureDigest)) {
    throw new PlatformError('unavailable', 'Composition media execution is no longer allowed', { retryable: false });
  }
  return { accepted, execution, deadlineAt };
}

/** The one composed media-child cancellation: the child must still be
 * outstanding and bound to exactly this V1 parent run (same creation version,
 * composition-proof authority and measured window). Runs inside the caller's
 * transaction after the caller has locked the parent row and decided the
 * parent no longer owns the child's work. Returns false when the child is
 * already terminal or bound elsewhere. */
export async function cancelBoundCompositionMediaChild(tx: DocTxOps, input: {
  parentJobId: string;
  parent: BuildJob;
  jobId: string;
  reason: string;
  finishedAt: ServerTimestampSentinel;
}): Promise<boolean> {
  const run = input.parent.measuredExecution;
  if (!run?.compositionDigest) return false;
  const child = await tx.get<AssetGenJob>(assetGenJobDoc(input.jobId));
  if (!child || !['awaiting-core','queued','running'].includes(child.status)
    || child.creationId !== input.parent.creationId || child.creationRun?.version !== input.parent.version
    || child.prerequisite?.kind !== 'composition-proof'
    || child.prerequisite.authorityDigest !== run.compositionDigest
    || child.measuredBudget?.buildJobId !== input.parentJobId
    || child.measuredBudget.acceptedAt !== run.acceptedAt
    || child.measuredBudget.deadlineAt !== run.deadlineAt) return false;
  await tx.update(assetGenJobDoc(input.jobId), {status:'failed',stage:null,variantCount:null,
    error:input.reason,failureKind:'cancelled',finishedAt:input.finishedAt});
  return true;
}

/** Fields to enqueue a new async generation job. The attribution four-tuple is
 *  captured by the callable and stored so completion can credit the member. */
export interface EnqueueAssetGenJobInput {
  jobId: string;
  creationId: string;
  kind: CreationAssetKind;
  name: string;
  prompt: string;
  createdBy: string;
  createdByHandle: string;
  actionPerformedBy: string;
  actionPerformedByHandle: string;
  /** Cover-only source choice; absent uses the game-assets default. */
  coverSource?: AssetGenJob['coverSource'];
  /** Platform-owned gallery refresh: attach the generated cover only when the
   * creator's cover is still the value captured by the immutable backfill plan. */
  autoAttachCover?: AssetGenJob['autoAttachCover'];
  /** Platform-managed title-overlay marker. */
  titleOverlay?: AssetGenJob['titleOverlay'];
  /** Platform-initiated art-completion marker (see {@link AssetGenJob.completion}). */
  completion?: AssetGenJob['completion'];
  /** Validated creator brief — REQUIRED on `music`/`sfx` jobs, forbidden
   *  otherwise (see {@link AssetGenJob.audioBrief}). */
  audioBrief?: AssetGenJob['audioBrief'];
  /** Strip-job payload — required on `kind: 'strip'` jobs, absent otherwise
   *  (see {@link AssetGenJob.strip}). */
  strip?: AssetGenJob['strip'];
  creationRun?: AssetGenJob['creationRun'];
  plan?: AssetGenJob['plan'];
  prerequisite?: AssetGenJob['prerequisite'];
  measuredBudget?: AssetGenJob['measuredBudget'];
}

export interface ReserveMeasuredMediaOperationInput {
  reservationId: string;
  operationId: string;
  kind: Extract<MeasuredBuildOperationKind, 'image-generation' | 'image-judge' | 'music-generation' | 'media-aux'>;
  provider: 'gemini' | 'lyria';
  model: string;
  requestId?: string;
  calls: number;
  costMicros: number;
  maxDurationMs: number;
}

/** `details.reason` of the refusal a measured media reservation gets when its
 * worst-case duration would end past the media (or parent) deadline. Nothing
 * was dispatched, so the producer ends that slot's attempts through its
 * normal failure path instead of treating the refusal as lost authority. */
export const MEASURED_MEDIA_DEADLINE_REFUSAL = 'measured-media-deadline' as const;

/** Non-terminal statuses — an "outstanding" job the dedupe/recovery flows care
 *  about (a Generate is in flight for this name). */
const OUTSTANDING: readonly AssetGenJob['status'][] = ['awaiting-core', 'queued', 'running'];

/** Stable prefix of a creator cancellation's terminal reason. ONE token, so the
 *  studio can tell "you stopped this" from "it broke" without a fifth status
 *  value (see {@link AssetGenJobsRepo.cancel}). */
export const ASSET_GEN_CANCELLED_PREFIX = 'cancelled by @';

/** The canonical cancellation reason for `handle`. */
export function assetGenCancelReason(handle: string): string {
  return `${ASSET_GEN_CANCELLED_PREFIX}${handle}`;
}

/** Whether a terminal job ended because a member cancelled it. */
export function wasAssetGenCancelled(job: Pick<AssetGenJob, 'status' | 'error'>): boolean {
  return job.status === 'failed' && (job.error ?? '').startsWith(ASSET_GEN_CANCELLED_PREFIX);
}

export class AssetGenJobsRepo {
  constructor(
    private readonly store: DocStorePort & Partial<DocTxRunner>,
    /** Injected clock for heartbeat/stale comparisons in tests. */
    private readonly now: () => number = () => Date.now(),
  ) {}

  /**
   * Enqueue a generation job in the `queued` state. Throws
   * `PlatformError('conflict')` if the id already exists.
   */
  async enqueue(input: EnqueueAssetGenJobInput): Promise<AssetGenJob> {
    if (await this.store.getDoc<AssetGenJob>(assetGenJobDoc(input.jobId))) {
      throw new PlatformError('conflict', `asset gen job ${input.jobId} already exists`);
    }
    await this.store.setDoc(assetGenJobDoc(input.jobId), this.queuedData(input));
    return (await this.get(input.jobId))!;
  }

  /** Used by Studio kickoff to commit the job and quota together. The caller
   * holds the creation lock before checking outstanding jobs. */
  async enqueueInTransaction(tx: DocTxOps, input: EnqueueAssetGenJobInput): Promise<void> {
    if (await tx.get<AssetGenJob>(assetGenJobDoc(input.jobId))) {
      throw new PlatformError('conflict', `asset gen job ${input.jobId} already exists`);
    }
    await tx.set(assetGenJobDoc(input.jobId), this.queuedData(input));
  }

  /** Automatic completion shares Studio's creation lock. Never schedule a
   * second producer for a static name or an already-covered animation state.
   * Manual jobs retain their attribution and publication behavior. */
  async enqueueCompletionIfUncovered(input: EnqueueAssetGenJobInput, maxStripStates?: number): Promise<{ jobId: string; stripStates: number } | null> {
    if (!input.completion) throw new PlatformError('validation', 'completion marker required');
    if (maxStripStates !== undefined && (!Number.isSafeInteger(maxStripStates) || maxStripStates < 1)) {
      throw new PlatformError('validation', 'strip budget must be a positive integer');
    }
    if (!this.store.runTransaction) throw new PlatformError('unavailable', 'completion enqueue requires server transactions');
    return this.store.runTransaction(async tx => {
      if (!await tx.get(creationDoc(input.creationId))) throw new PlatformError('not-found', 'creation not found');
      if (!tx.query) throw new PlatformError('internal', 'completion enqueue requires transactional queries');
      const pending = (await tx.query<AssetGenJob>(assetGenJobsCollection(), {
        where: [['creationId', '==', input.creationId], ['kind', '==', input.kind]],
      })).filter(job => OUTSTANDING.includes(job.status));
      let next = input;
      if (input.kind === 'strip') {
        const strip = input.strip;
        if (!strip) throw new PlatformError('validation', 'strip payload required');
        const states = strip.states.filter(state => !pending.some(job =>
          job.strip ? job.strip.entityName === strip.entityName && job.strip.states.includes(state)
            : job.name === strip.entityName)).slice(0, maxStripStates);
        if (!states.length) return null;
        next = { ...input, strip: { ...strip, states },
          prompt: `Animation strips for "${strip.entityName}" (${states.join(', ')})` };
      } else if (pending.some(job => job.name === input.name)) return null;
      await this.enqueueInTransaction(tx, next);
      return { jobId: input.jobId, stripStates: next.strip?.states.length ?? 0 };
    });
  }

  private queuedData(input: EnqueueAssetGenJobInput) {
    // The audio invariant lives in the ONE queue writer (and in the table's
    // check constraint): an audio job is meaningless without the brief its
    // provider request is built from, and a visual job must never carry one.
    const audio = input.kind === 'music' || input.kind === 'sfx';
    if (audio && !input.audioBrief) {
      throw new PlatformError('validation', `${input.kind} generation requires an audio brief`);
    }
    if (!audio && input.audioBrief) {
      throw new PlatformError('validation', `${input.kind} generation cannot carry an audio brief`);
    }
    if (input.plan && input.creationRun?.branch !== 'visual') {
      throw new PlatformError('validation', 'visual plan jobs require a visual creation-run marker');
    }
    if (input.creationRun?.branch === 'visual' && !input.plan) {
      throw new PlatformError('validation', 'visual creation-run jobs require plan metadata');
    }
    if (input.creationRun?.branch === 'music' && (input.kind !== 'music' || !input.audioBrief)) {
      throw new PlatformError('validation', 'music creation-run jobs require a music brief');
    }
    return {
      creationId: input.creationId,
      kind: input.kind,
      name: input.name,
      prompt: input.prompt,
      audioBrief: input.audioBrief ?? null,
      creationRun: input.creationRun ?? null,
      plan: input.plan ?? null,
      prerequisite: input.prerequisite ?? null,
      measuredBudget: input.measuredBudget ?? null,
      coverSource: input.coverSource ?? null,
      autoAttachCover: input.autoAttachCover ?? null,
      titleOverlay: input.titleOverlay ?? null,
      completionRecoveryEligible: true,
      completionReconciledAt: null,
      completionCheckedAt: 0,
      status: input.prerequisite ? 'awaiting-core' : 'queued',
      stage: null,
      workerId: null,
      heartbeatAt: null,
      attempts: 0,
      createdBy: input.createdBy,
      createdByHandle: input.createdByHandle,
      actionPerformedBy: input.actionPerformedBy,
      actionPerformedByHandle: input.actionPerformedByHandle,
      variantCount: null,
      error: null,
      failureKind: null,
      traceGcsUri: '',
      completion: input.completion ?? null,
      strip: input.strip ?? null,
      createdAt: this.store.serverTimestamp(),
      finishedAt: null,
    };
  }

  /** Fetch a job, or null. */
  async get(jobId: string): Promise<AssetGenJob | null> {
    return this.store.getDoc<AssetGenJob>(assetGenJobDoc(jobId));
  }

  /** Reserve provider exposure on the parent build ledger while holding locks
   * in the global build -> asset order. Historical/interactive jobs have no
   * shared authority and return null so their existing quota path is unchanged. */
  async reserveMeasuredProviderOperation(
    jobId: string,
    workerId: string,
    input: ReserveMeasuredMediaOperationInput,
  ): Promise<MeasuredBuildReservationV1 | null> {
    if (!this.store.runTransaction) {
      const job = await this.get(jobId);
      if (!job?.measuredBudget) return null;
      throw new PlatformError('unavailable', 'measured media reservation requires server transactions');
    }
    if (!Number.isSafeInteger(input.calls) || input.calls < 1
      || !Number.isSafeInteger(input.costMicros) || input.costMicros < 0
      || !Number.isSafeInteger(input.maxDurationMs) || input.maxDurationMs < 1) {
      throw new PlatformError('validation', 'invalid measured media reservation');
    }
    const observed = await this.get(jobId);
    if (!observed?.measuredBudget) return null;
    const observedBuildJobId = observed.measuredBudget.buildJobId;
    const observedCompositionRole = observed.measuredBudget.role;
    const observedCompositionFields = [observed.measuredBudget.role,
      observed.measuredBudget.authorityDigest, observed.measuredBudget.executionDigest];
    if (observedCompositionFields.some(value => value !== undefined)
      && observedCompositionFields.some(value => value === undefined)) {
      throw new PlatformError('conflict', 'measured media composition authority is incomplete');
    }
    return this.store.runTransaction(async tx => {
      // Released media reads the singleton execution-revocation control first.
      // Thereafter preserve the global build -> version -> child order shared
      // with proof sealing and publication.
      const controlStored = observedCompositionRole
        ? await tx.get<Record<string, unknown>>(compositionRuntimeControlDoc())
        : null;
      const parent = await tx.get<BuildJob>(buildJobDoc(observedBuildJobId));
      const version = observedCompositionRole && parent
        ? await tx.get<CreationVersion>(versionDoc(parent.creationId, parent.version))
        : null;
      const job = await tx.get<AssetGenJob>(assetGenJobDoc(jobId));
      const authority = job?.measuredBudget;
      const run = parent?.measuredExecution;
      if (!parent || !job || !authority || !run
        || authority.buildJobId !== observedBuildJobId
        || authority.acceptedAt !== run.acceptedAt
        || authority.deadlineAt !== run.deadlineAt) {
        throw new PlatformError('conflict', 'measured media parent authority changed');
      }
      if (job.status !== 'running' || job.workerId !== workerId) {
        throw new PlatformError('conflict', `measured media job ${jobId} claim lost`);
      }
      let compositionExecution: CompositionMediaExecutionV1 | undefined;
      if (observedCompositionRole) {
        if (observedCompositionRole !== 'composition-media@2') {
          throw new PlatformError('unavailable', 'Composition media execution authority is unavailable', { retryable: false });
        }
        compositionExecution = (await validateFundedCompositionMediaContext({
          controlStored, parent, version, job, jobId, workerId,
          expectedBuildJobId: observedBuildJobId, expectedRole: observedCompositionRole, now: this.now(),
        })).execution;
        const route = compositionMediaRouteV1(compositionExecution, input.kind);
        // The image CLI generates a bounded batch per step; its call count is
        // charged against the route allowance below.
        if (!route || input.calls < 1 || input.provider !== route.provider || input.model !== route.model
          || input.maxDurationMs > route.maxDurationMs
          || input.costMicros !== route.maxCostMicrosPerCall * input.calls) {
          throw new PlatformError('validation', 'Composition media provider route changed');
        }
      }
      const existing = run.reservations[input.reservationId];
      const claimAttempt = job.attempts ?? 0;
      if (run.compositionDigest || job.prerequisite?.kind === 'composition-proof') {
        const barrier = run.compositionBarrier;
        const prerequisite = job.prerequisite;
        if (!barrier || prerequisite?.kind !== 'composition-proof'
          || barrier.authorityDigest !== run.compositionDigest || prerequisite.authorityDigest !== run.compositionDigest
          || !barrier.mediaJobIds.includes(jobId) || prerequisite.mediaBasisDigest !== barrier.mediaBasisDigest
          || prerequisite.releasedAt !== barrier.committedAt) {
          throw new PlatformError('conflict', 'Composition media requires its committed proof barrier');
        }
        const prior = Object.values(run.reservations).filter(item => item.mediaJobId === jobId);
        // An uncertain dispatch blocks further calls in ITS lane: the
        // soundtrack's one call and the image work are independent providers
        // with independent allowances, run side by side, so an uncertain
        // soundtrack can never be duplicated by an image call (or vice versa).
        const lane = (kind: string) => kind === 'music-generation';
        if (prior.some(item => (item.status === 'uncertain' && lane(item.kind) === lane(input.kind)) || item.status === 'reserved'
          && (item.mediaClaimAttempt !== claimAttempt || item.mediaWorkerId !== workerId))) {
          throw new PlatformError('conflict', 'Composition media has uncertain dispatch; reconciliation is required before another call');
        }
        // A lost reservation response is not proof that dispatch never began.
        // Returning an old ticket could authorize a duplicate provider call.
        // Retained media is joined, not regenerated under a new ticket.
        if (existing || prior.some(item => item.operationId === input.operationId)) {
          throw new PlatformError('conflict', 'Composition media operation already reserved; reuse retained evidence or reconcile');
        }
        if (!compositionExecution || !observedCompositionRole) {
          throw new PlatformError('quota', 'Composition media has no accepted provider allowance', { retryable: false });
        }
        const route = compositionMediaRouteV1(compositionExecution, input.kind);
        const kindCalls = prior.filter(item => item.kind === input.kind)
          .reduce((sum, item) => sum + item.reservedCalls, 0);
        if (!route || kindCalls + input.calls > route.maxCalls) {
          throw new PlatformError('quota', 'Composition media route call allowance exhausted', { retryable: false });
        }
      }
      if (existing) {
        const matches = existing.operationId === input.operationId
          && existing.kind === input.kind
          && existing.provider === input.provider
          && existing.model === input.model
          && existing.requestId === input.requestId
          && existing.reservedCalls === input.calls
          && existing.reservedCostMicros === input.costMicros
          && existing.maxDurationMs === input.maxDurationMs
          && existing.mediaJobId === jobId
          && existing.mediaClaimAttempt === claimAttempt
          && existing.mediaWorkerId === workerId;
        if (!matches) throw new PlatformError('conflict', 'measured media reservation id was reused');
        return structuredClone(existing);
      }
      const now = this.now();
      if (parent.status === 'done' || parent.status === 'failed') {
        throw new PlatformError('conflict', 'measured build is terminal; new media dispatch is forbidden');
      }
      if (compositionExecution && job.prerequisite?.kind === 'composition-proof') {
        const mediaDeadlineAt = compositionMediaDeadlineAt({
          execution: compositionExecution,
          parentDeadlineAt: run.deadlineAt,
          finishReserveMs: run.limits.finishReserveMs,
          releasedAt: job.prerequisite.releasedAt!,
        });
        if (this.now() + input.maxDurationMs > mediaDeadlineAt) {
          throw new PlatformError('unavailable', 'Composition media has insufficient wall time for dispatch', {
            retryable: false, details: { reason: MEASURED_MEDIA_DEADLINE_REFUSAL },
          });
        }
      }
      if (now + input.maxDurationMs > run.deadlineAt - run.limits.finishReserveMs) {
        throw new PlatformError('unavailable', 'measured build has insufficient wall time for media dispatch', {
          retryable: false, details: { reason: MEASURED_MEDIA_DEADLINE_REFUSAL },
        });
      }
      if (run.usage.reservedProviderCalls + input.calls > run.limits.maxProviderCalls
        || run.usage.reservedCostMicros + input.costMicros > run.limits.maxCostMicros) {
        throw new PlatformError('quota', `measured build budget exhausted by media dispatch: provider calls ${run.usage.reservedProviderCalls} + ${input.calls} / ${run.limits.maxProviderCalls}; reserved cost ${run.usage.reservedCostMicros} + ${input.costMicros} / ${run.limits.maxCostMicros} micros`, { retryable: false });
      }
      const reservation: MeasuredBuildReservationV1 = {
        reservationId: input.reservationId,
        operationId: input.operationId,
        kind: input.kind,
        status: 'reserved',
        provider: input.provider,
        model: input.model,
        ...(input.requestId ? { requestId: input.requestId } : {}),
        reservedCalls: input.calls,
        reservedCostMicros: input.costMicros,
        maxDurationMs: input.maxDurationMs,
        reservedAt: now,
        mediaJobId: jobId,
        mediaClaimAttempt: claimAttempt,
        mediaWorkerId: workerId,
      };
      await tx.update(buildJobDoc(authority.buildJobId), {
        measuredExecution: {
          ...run,
          ledgerRevision: (run.ledgerRevision ?? 0) + 1,
          usage: {
            ...run.usage,
            reservedProviderCalls: run.usage.reservedProviderCalls + input.calls,
            reservedCostMicros: run.usage.reservedCostMicros + input.costMicros,
          },
          reservations: { ...run.reservations, [input.reservationId]: reservation },
        },
      });
      return reservation;
    });
  }

  /** Settle an already reserved attempt. Parent terminal state and a later
   * child requeue do not erase spend that may already have reached a provider. */
  async settleMeasuredProviderOperation(
    jobId: string,
    workerId: string,
    reservationId: string,
    actualCostMicros: number,
  ): Promise<void> {
    await this.finishMeasuredProviderOperation(jobId, workerId, reservationId, {
      status: 'settled', actualCostMicros,
    });
  }

  async markMeasuredProviderOperationUncertain(
    jobId: string,
    workerId: string,
    reservationId: string,
    error: string,
  ): Promise<void> {
    await this.finishMeasuredProviderOperation(jobId, workerId, reservationId, {
      status: 'uncertain', error: error.slice(0, 500),
    });
  }

  private async finishMeasuredProviderOperation(
    jobId: string,
    workerId: string,
    reservationId: string,
    outcome: { status: 'settled'; actualCostMicros: number } | { status: 'uncertain'; error: string },
  ): Promise<void> {
    if (!this.store.runTransaction) throw new PlatformError('unavailable', 'measured media settlement requires server transactions');
    if (outcome.status === 'settled' && (!Number.isSafeInteger(outcome.actualCostMicros) || outcome.actualCostMicros < 0)) {
      throw new PlatformError('validation', 'invalid measured media settlement');
    }
    const observed = await this.get(jobId);
    const observedBuildJobId = observed?.measuredBudget?.buildJobId;
    if (!observedBuildJobId) throw new PlatformError('conflict', 'measured media authority is unavailable');
    await this.store.runTransaction(async tx => {
      const parent = await tx.get<BuildJob>(buildJobDoc(observedBuildJobId));
      // Preserve lock order even though settlement authorization is carried by
      // the immutable reservation rather than the job's current claim.
      const job = await tx.get<AssetGenJob>(assetGenJobDoc(jobId));
      const authority = job?.measuredBudget;
      const run = parent?.measuredExecution;
      const reservation = run?.reservations[reservationId];
      if (!authority || authority.buildJobId !== observedBuildJobId || !run || !reservation
        || reservation.mediaJobId !== jobId || reservation.mediaWorkerId !== workerId) {
        throw new PlatformError('conflict', 'measured media settlement authority changed');
      }
      if (reservation.status === outcome.status) {
        if (outcome.status === 'settled' && reservation.actualCostMicros !== outcome.actualCostMicros) {
          throw new PlatformError('conflict', 'measured media settlement changed');
        }
        return;
      }
      if (reservation.status !== 'reserved') throw new PlatformError('conflict', 'measured media reservation is already final');
      const charged = outcome.status === 'settled' ? outcome.actualCostMicros : reservation.reservedCostMicros;
      if (charged > reservation.reservedCostMicros) {
        throw new PlatformError('conflict', 'media provider cost exceeded its durable reservation');
      }
      const finalReservation: MeasuredBuildReservationV1 = outcome.status === 'settled'
        ? { ...reservation, status: 'settled', actualCostMicros: charged, settledAt: this.now() }
        : { ...reservation, status: 'uncertain', error: outcome.error, settledAt: this.now() };
      await tx.update(buildJobDoc(authority.buildJobId), {
        measuredExecution: {
          ...run,
          ledgerRevision: (run.ledgerRevision ?? 0) + 1,
          usage: outcome.status === 'settled' ? {
            ...run.usage,
            settledProviderCalls: run.usage.settledProviderCalls + reservation.reservedCalls,
            settledCostMicros: run.usage.settledCostMicros + charged,
            reservedCostMicros: run.usage.reservedCostMicros - reservation.reservedCostMicros + charged,
          } : run.usage,
          reservations: { ...run.reservations, [reservationId]: finalReservation },
        },
      });
    });
  }

  /** Atomically open an accepted media wave after the claim-owning build has
   * produced its verified core. A repeated call with the same identity/basis
   * is harmless; a changed identity or basis is a hard conflict. */
  async releaseAfterVerifiedCore(input: {
    jobIds: readonly string[];
    creationId: string;
    runInputDigest: string;
    mediaBasisDigest: string;
  }): Promise<number> {
    if (!this.store.runTransaction) {
      throw new PlatformError('unavailable', 'verified-core media release requires server transactions');
    }
    const ids = [...new Set(input.jobIds)].sort();
    if (!input.runInputDigest || !input.mediaBasisDigest || ids.length !== input.jobIds.length) {
      throw new PlatformError('validation', 'verified-core media release requires unique jobs and exact digests');
    }
    return this.store.runTransaction(async tx => {
      const rows: Array<{ id: string; job: AssetGenJob }> = [];
      for (const id of ids) {
        const job = await tx.get<AssetGenJob>(assetGenJobDoc(id));
        if (!job) throw new PlatformError('not-found', `verified-core media job ${id} not found`);
        const prerequisite = job.prerequisite;
        if (job.creationId !== input.creationId
          || prerequisite?.kind !== 'verified-core'
          || prerequisite.runInputDigest !== input.runInputDigest) {
          throw new PlatformError('conflict', `verified-core media job ${id} has a different origin`);
        }
        if (prerequisite.mediaBasisDigest && prerequisite.mediaBasisDigest !== input.mediaBasisDigest) {
          throw new PlatformError('conflict', `verified-core media job ${id} is bound to a different core`);
        }
        rows.push({ id, job });
      }
      let released = 0;
      for (const { id, job } of rows) {
        if (job.status !== 'awaiting-core') continue;
        await tx.update(assetGenJobDoc(id), {
          status: 'queued',
          prerequisite: {
            kind: 'verified-core',
            runInputDigest: input.runInputDigest,
            mediaBasisDigest: input.mediaBasisDigest,
            releasedAt: this.store.serverTimestamp(),
          },
        });
        released += 1;
      }
      return released;
    });
  }

  /** Adopt an independently-running creation-plan job into the late-art
   * completion channel after the code build exhausts its bounded wait. This is
   * a metadata-only CAS: the original provider request keeps its claim and no
   * replacement producer is enqueued. */
  async armCreationRunCompletion(
    jobId: string,
    originVersion: number,
    expectedPriorOrigins: readonly number[] = [],
  ): Promise<boolean> {
    if (!Number.isSafeInteger(originVersion) || originVersion < 1) {
      throw new PlatformError('validation', 'completion origin version must be positive');
    }
    const current = await this.get(jobId);
    if (!current
      || current.creationRun?.branch !== 'visual'
      || !current.plan
      || current.status === 'failed') return false;
    if (current.completion?.originVersion === originVersion) return true;
    const transferablePrior = current.completion
      && current.completionReconciledAt == null
      && expectedPriorOrigins.includes(current.completion.originVersion)
      && expectedPriorOrigins.includes(current.creationRun.version);
    if (current.completion && !transferablePrior) {
      throw new PlatformError('conflict', 'creation-run visual job is armed for another version');
    }
    const won = await this.store.guardedUpdate<AssetGenJob>(
      assetGenJobDoc(jobId),
      job => job !== null
        && job.creationRun?.branch === 'visual'
        && !!job.plan
        && job.status !== 'failed'
        && (job.completion == null || (
          job.completionReconciledAt == null
          && expectedPriorOrigins.includes(job.completion.originVersion)
          && expectedPriorOrigins.includes(job.creationRun.version)
        )),
      {
        completion: { originVersion, variants: 1 },
        completionRecoveryEligible: true,
        completionReconciledAt: null,
        completionCheckedAt: 0,
      },
    );
    if (won) return true;
    const latest = await this.get(jobId);
    if (latest?.completion?.originVersion === originVersion) return true;
    if (!latest || latest.status === 'failed') return false;
    throw new PlatformError('conflict', 'creation-run visual completion authority changed while arming');
  }

  /**
   * Claim a specific queued job for `workerId` — a race-free compare-and-set on
   * `status == 'queued'` ({@link DocStorePort.guardedUpdate}, one transaction).
   * Returns the claimed job (with its id) or null when the job is gone or a
   * concurrent worker already claimed it (the loser's guard fails).
   */
  async claim(jobId: string, workerId: string): Promise<(AssetGenJob & { id: string }) | null> {
    const won = await this.store.guardedUpdate<AssetGenJob>(
      assetGenJobDoc(jobId),
      (job) => job !== null && job.status === 'queued',
      {
        status: 'running',
        stage: 'generating',
        workerId,
        heartbeatAt: this.store.serverTimestamp(),
      },
    );
    if (!won) return null;
    const claimed = await this.get(jobId);
    if (!claimed) return null;
    return { ...claimed, id: jobId };
  }

  /**
   * Refresh a running job's heartbeat, optionally advancing its `stage`. Throws
   * `PlatformError('conflict')` if the job isn't running under `workerId` (a
   * stolen/expired claim).
   */
  async heartbeat(jobId: string, workerId: string, stage?: AssetGenStage): Promise<void> {
    const won = await this.store.guardedUpdate<AssetGenJob>(assetGenJobDoc(jobId),
      job => job !== null && job.status === 'running' && job.workerId === workerId,
      { heartbeatAt: this.store.serverTimestamp(), ...(stage !== undefined ? { stage } : {}) });
    if (!won) throw new PlatformError('conflict', `heartbeat: asset gen job ${jobId} not running under ${workerId}`);
  }

  /**
   * Mark a running job succeeded with the count of variants it produced and the
   * `gs://` of its generation trace ('' when tracing was unavailable). Only
   * the owning `workerId` may complete it. Clears `stage`, sets `finishedAt`.
   */
  async succeed(
    jobId: string,
    workerId: string,
    variantCount: number,
    traceGcsUri: string,
  ): Promise<void> {
    await this.terminate(jobId, workerId, 'succeeded', { variantCount, error: null, traceGcsUri });
  }

  /** Publish an async job's staged candidates and its success terminal as ONE
   * transaction. A cancellation/reap winning before this transaction leaves
   * every candidate staged and therefore invisible; no check/append gap exists. */
  async succeedWithStagedVariants(
    jobId: string,
    workerId: string,
    creationId: string,
    variantIds: readonly string[],
    traceGcsUri: string,
    /** Composition media only: the strip slots this child publishes without
     * art (see {@link CompositionDeferredMediaV1}); [] for every other job. */
    deferred: readonly CompositionDeferredMediaV1[],
  ): Promise<void> {
    if (!this.store.runTransaction) {
      throw new PlatformError('unavailable', 'atomic candidate publication requires server transactions');
    }
    const ids = [...new Set(variantIds)].sort();
    if (ids.length !== variantIds.length) {
      throw new PlatformError('validation', 'generated candidate ids must be unique');
    }
    const observed = await this.get(jobId);
    const observedCompositionFields = [observed?.measuredBudget?.role,
      observed?.measuredBudget?.authorityDigest, observed?.measuredBudget?.executionDigest];
    if (observedCompositionFields.some(value => value !== undefined)
      && observedCompositionFields.some(value => value === undefined)) {
      throw new PlatformError('conflict', 'measured media composition authority is incomplete');
    }
    const compositionHint = observed?.prerequisite?.kind === 'composition-proof'
      || observedCompositionFields.some(value => value !== undefined);
    const observedBuildJobId = observed?.measuredBudget?.buildJobId;
    if (compositionHint && !observedBuildJobId) {
      throw new PlatformError('conflict', 'Composition media parent authority is unavailable');
    }
    await this.store.runTransaction(async tx => {
      // Funded composition locks the revocation control and live parent before
      // the public creation/variant rows. Legacy publication retains the
      // established creation -> child -> variants order.
      const controlStored = compositionHint
        ? await tx.get<Record<string, unknown>>(compositionRuntimeControlDoc())
        : null;
      const parent = compositionHint
        ? await tx.get<BuildJob>(buildJobDoc(observedBuildJobId!))
        : null;
      const creation = await tx.get<Creation>(creationDoc(creationId));
      if (!creation) throw new PlatformError('not-found', `creation ${creationId} not found`);
      const version = compositionHint && parent
        ? await tx.get<CreationVersion>(versionDoc(parent.creationId, parent.version))
        : null;
      const job = await tx.get<AssetGenJob>(assetGenJobDoc(jobId));
      if (!job || job.status !== 'running' || job.workerId !== workerId || job.creationId !== creationId) {
        throw new PlatformError('conflict', `succeeded: asset gen job ${jobId} claim lost`);
      }
      let fundedContext: FundedCompositionMediaContext | undefined;
      let acceptedComposition: FundedCompositionMediaContext['accepted'] | undefined;
      let deferredSlots: CompositionDeferredMediaV1[] = [];
      // A revision's slots that carry its predecessor's published art: the
      // child owes them no candidate (they are bound from the version's carry).
      let carriedSlots: string[] = [];
      if (deferred.length && !compositionHint) {
        throw new PlatformError('validation', 'Only a composition media child can defer slots');
      }
      if (compositionHint) {
        const identity = (row: AssetGenJob) => ({
          measuredBudget: row.measuredBudget ?? null, prerequisite: row.prerequisite ?? null,
          creationRun: row.creationRun ?? null, plan: row.plan ?? null, attempts: row.attempts ?? 0,
          creationId: row.creationId, kind: row.kind, name: row.name,
        });
        if (!observed?.measuredBudget
          || canonicalJson(identity(job)) !== canonicalJson(identity(observed))) {
          throw new PlatformError('conflict', 'Composition media child identity changed before publication');
        }
        if (!parent || !version) {
          throw new PlatformError('conflict', 'Composition media version authority is unavailable');
        }
        const { authority: accepted, revision } = await validateCompositionRoute(parent, version, observedBuildJobId!);
        acceptedComposition = accepted;
        carriedSlots = (revision?.carry ?? []).map(item => item.slot);
        try {
          deferredSlots = validateCompositionDeferredMediaV1(structuredClone([...deferred]), accepted.media?.slots ?? [], accepted.spec);
        } catch {
          throw new PlatformError('validation', 'Composition media deferred slots are not deferrable slots of this authority');
        }
        if (accepted.media?.execution) {
          fundedContext = await validateFundedCompositionMediaContext({
            controlStored, parent, version, job, jobId, workerId,
            expectedBuildJobId: observedBuildJobId!,
            expectedRole: 'composition-media@2', now: this.now(),
          });
        } else if (observedCompositionFields.some(value => value !== undefined)) {
          throw new PlatformError('conflict', 'Zero-provider composition media cannot carry funding markers');
        }
      }
      const resumeSources = new Set<string>();
      let carriedProviderRefusedMusic = false;
      if (job.plan?.resumeFromJobId) {
        if (job.creationRun?.branch !== 'visual') {
          throw new PlatformError('conflict', 'visual resume authority is attached to a non-visual job');
        }
        if (job.measuredBudget?.role === 'composition-media@2') {
          if (!acceptedComposition) throw new PlatformError('conflict', 'Composition media authority is unavailable');
          let journal: string[];
          try {
            journal = await compositionMediaResumeJournal({
              getMediaJob: id => tx.get<AssetGenJob>(assetGenJobDoc(id)),
              getBuildJob: id => tx.get<BuildJob>(buildJobDoc(id)),
              getVersion: (creationId, version) => tx.get<CreationVersion>(versionDoc(creationId, version)),
            }, {...job,id:jobId},acceptedComposition);
          } catch {
            throw new PlatformError('conflict', 'visual media resume predecessor no longer matches immutable authority');
          }
          for (const id of journal.slice(1)) {
            resumeSources.add(id);
            if (audioBriefRefusalDetail((await tx.get<AssetGenJob>(assetGenJobDoc(id)))?.error) !== null) {
              carriedProviderRefusedMusic = true;
            }
          }
        } else {
          let nextId: string | undefined = job.plan.resumeFromJobId;
          let newerVersion = job.creationRun.version;
          for (let depth = 0; nextId && depth < MAX_VISUAL_RESUME_CHAIN; depth += 1) {
            const priorId: string = nextId;
            const prior: AssetGenJob | null = await tx.get<AssetGenJob>(assetGenJobDoc(priorId));
            if (!prior || (prior.status !== 'succeeded' && prior.status !== 'failed')
              || prior.creationId !== creationId || prior.creationRun?.branch !== 'visual'
              || prior.creationRun.designRev !== job.creationRun.designRev
              || prior.creationRun.version >= newerVersion || prior.createdBy !== job.createdBy
              || prior.plan?.orientation !== job.plan.orientation
              || JSON.stringify(prior.plan?.assetNames ?? []) !== JSON.stringify(job.plan.assetNames ?? [])) {
              throw new PlatformError('conflict', 'visual media resume predecessor no longer matches immutable authority');
            }
            resumeSources.add(priorId);
            newerVersion = prior.creationRun.version;
            nextId = prior.plan?.resumeFromJobId;
          }
          if (nextId) {
            throw new PlatformError('conflict', `visual media resume chain exceeds ${MAX_VISUAL_RESUME_CHAIN}`);
          }
        }
      }
      const candidates: Array<{ path: string; row: AssetVariantDoc }> = [];
      for (const variantId of ids) {
        const path = assetVariantDoc(creationId, variantId);
        const row = await tx.get<AssetVariantDoc>(path);
        const ownedByPublisher = row?.genJobId === jobId;
        const retainedResumeMilestone = !!row?.genJobId && resumeSources.has(row.genJobId);
        if (!row || row.creationId !== creationId || row.createdBy !== job.createdBy
          || (!ownedByPublisher && !retainedResumeMilestone)
          || (ownedByPublisher ? row.exposureState !== 'staged' : !isReusableCompositionJournalRowV1(row, jobId))
          || row.reviewState === 'superseded') {
          throw new PlatformError('conflict', `candidate ${variantId} is not staged for asset gen job ${jobId}`);
        }
        if (row.reviewState === 'rejected') {
          throw new PlatformError('conflict', 'these exact media bytes were rejected for this creation');
        }
        await new AssetRejectionsRepo(this.store).assertAllowed(
          creationId,
          row.contentSha256,
          creation.assetPolicyRevision ?? 0,
          tx,
        );
        candidates.push({ path, row });
      }
      if (fundedContext) {
        const slots = fundedContext.accepted.media!.slots;
        const reservations = Object.values(parent!.measuredExecution!.reservations)
          .filter(reservation => reservation.mediaJobId === jobId);
        const generationCalls = reservations.filter(reservation => reservation.kind === 'image-generation')
          .reduce((sum, reservation) => sum + reservation.reservedCalls, 0);
        const judgeCalls = reservations.filter(reservation => reservation.kind === 'image-judge')
          .reduce((sum, reservation) => sum + reservation.reservedCalls, 0);
        const musicCalls = reservations.filter(reservation => reservation.kind === 'music-generation')
          .reduce((sum, reservation) => sum + reservation.reservedCalls, 0);
        // Without a resume journal the exact first pass is always spent; per-
        // slot recovery (bounded by the derived allowance) may add
        // generations and their verdicts. A resumed child, like a revision's
        // child carrying its predecessor's art, spends at least one
        // generation and one verdict per slot it did not reuse or carry.
        const plan = compositionMediaCallPlanV1(fundedContext.accepted.spec, { music: slots.includes(COMPOSITION_MUSIC_SLOT) });
        // A derived slot is recoloured from its source's accepted art: it is
        // neither generated nor judged.
        const derived = compositionDerivedSlotsV1(fundedContext.accepted.spec);
        const freshRows = candidates.filter(({ row }) => row.genJobId === jobId).map(({ row }) => row);
        const fresh = freshRows.filter(row => !derived.has(row.name)).map(row => row.kind);
        const partialPlan = freshRows.length < candidates.length || carriedSlots.length > 0;
        const musicDeferral = deferredSlots.find(item => item.slot === COMPOSITION_MUSIC_SLOT)?.reason;
        // A deferred soundtrack spent its one call, unless a predecessor's
        // refusal was carried instead of asking again.
        const musicDispatched = fresh.includes('music')
          || (musicDeferral !== undefined && !(musicDeferral === 'provider-refused' && carriedProviderRefusedMusic));
        // A cover the provider could not make is a byte copy of accepted art
        // (compositionCoverStandIn): no generation was spent on it.
        const cover = candidates.find(({ row }) => row.kind === 'cover')?.row;
        const coverStandIn = !!cover && isCompositionCoverStandInV1(cover, candidates.map(({ row }) => row));
        // A strip the media window refused may never have been dispatched. A
        // deferred base sprite also prevents its strips from being generated
        // because there is no accepted reference image.
        const partialFirstPass = partialPlan || coverStandIn || deferredSlots.some(item => item.reason === 'media-window'
          || compositionMediaSlotKindV1(item.slot) === 'sprite');
        const minimumGenerations = partialFirstPass ? Math.min(1, fresh.filter(kind => kind !== 'music').length) : plan.imageCalls;
        // A deferred strip may never have produced an image to judge.
        const minimumVerdicts = partialFirstPass
          ? fresh.filter(kind => kind === 'sprite' || kind === 'background' || kind === 'strip').length
          : plan.judgedImages - deferredSlots.filter(item => !derived.has(item.slot)).length;
        // Every accepted slot is published, carried or (deferrable only)
        // deferred — exactly one of them.
        if (canonicalJson([...candidates.map(candidate => candidate.row.name), ...carriedSlots, ...deferredSlots.map(item => item.slot)].sort())
            !== canonicalJson([...slots].sort())
          || candidates.some(({ row }) => row.kind !== compositionMediaSlotKindV1(row.name)
            || !(row.genJobId === jobId || (!!row.genJobId && resumeSources.has(row.genJobId)))
            || (row.kind === 'strip') !== !!row.strip
            || (row.strip && (!isAnimStateName(row.strip.state) || stripAssetName(row.strip.entityName, row.strip.state) !== row.name))
            || (row.kind === 'music') !== !!row.audio)
          // Only a soundtrack deferred as unavailable may end on an uncertain
          // call (a timeout or lost connection), charged its full reservation.
          || reservations.some(reservation => (reservation.status !== 'settled'
              && !(reservation.status === 'uncertain' && reservation.kind === 'music-generation'
                && musicDeferral === 'provider-unavailable'))
            || !['image-generation', 'image-judge', 'music-generation'].includes(reservation.kind))
          || generationCalls < minimumGenerations || generationCalls > fundedContext.execution.generation.maxCalls
          || judgeCalls < minimumVerdicts || judgeCalls > fundedContext.execution.judge.maxCalls
          || musicCalls !== (musicDispatched ? plan.musicCalls : 0)) {
          throw new PlatformError('conflict', 'Composition media evidence is incomplete or changed before publication');
        }
      }
      // Managed cover/title jobs carry required publication side effects. They
      // share this transaction with candidate activation + terminal success,
      // so cancellation cannot leave a cover/title mutation behind and a
      // side-effect failure cannot produce a false succeeded job.
      if (job.autoAttachCover) {
        const candidate = candidates.find(({ row }) => row.kind === 'cover' && row.name === 'cover')?.row;
        if (!candidate) throw new PlatformError('validation', 'gallery cover refresh produced no cover variant');
        if ((creation.coverUrl ?? null) === job.autoAttachCover.expectedCoverUrl) {
          await new AssetRejectionsRepo(this.store).assertAllowed(
            creationId,
            candidate.contentSha256,
            creation.assetPolicyRevision ?? 0,
            tx,
          );
          const asset: CreationAsset = {
            variantId: candidate.variantId,
            kind: 'cover',
            ...(candidate.contentSha256 ? { contentSha256: candidate.contentSha256 } : {}),
            name: 'cover',
            url: candidate.url,
            prompt: candidate.prompt,
            attachedBy: job.createdBy,
            attachedByHandle: job.createdByHandle,
            attachedAt: this.now(),
            source: 'studio',
          };
          const assets = creation.assets ?? [];
          const index = assets.findIndex(existing => (existing.name ?? '').toLowerCase() === 'cover');
          const prior = index < 0 ? null : assets[index];
          if (prior && prior.kind !== 'cover') {
            throw new PlatformError('conflict', 'gallery cover name is attached as a non-cover asset');
          }
          if (prior?.variantId !== candidate.variantId) {
            const next = assets.slice();
            if (index < 0) next.push(asset);
            else next[index] = asset;
            await tx.update(creationDoc(creationId), {
              assets: next,
              coverUrl: candidate.url,
              updatedAt: this.store.serverTimestamp(),
            });
          }
        }
      }
      if (job.titleOverlay && creation.title === job.titleOverlay.expectedTitle) {
        const candidate = candidates.find(({ row }) => row.kind === 'sprite' && row.name === 'title-overlay')?.row;
        if (!candidate) throw new PlatformError('validation', 'title-overlay generation produced no usable overlay');
        await tx.update(creationDoc(creationId), {
          titleOverlayUrl: candidate.url,
          titleOverlayForTitle: job.titleOverlay.expectedTitle,
        });
      }
      for (const candidate of candidates) {
        await tx.update(candidate.path, { exposureState: 'active' });
      }
      await tx.update(assetGenJobDoc(jobId), {
        status: 'succeeded',
        stage: null,
        variantCount: ids.length,
        error: null,
        failureKind: null,
        traceGcsUri,
        ...(deferredSlots.length ? { deferredSlots } : {}),
        finishedAt: this.store.serverTimestamp(),
      });
    });
  }

  /**
   * Mark a running job failed with a reason and the trace `gs://` ('' when
   * tracing was unavailable). Only the owning `workerId` may fail it. Clears
   * `stage`, sets `finishedAt` and `error`.
   */
  async fail(jobId: string, workerId: string, error: string, traceGcsUri: string): Promise<void> {
    await this.terminate(jobId, workerId, 'failed', {
      variantCount: null,
      error,
      traceGcsUri,
      failureKind: 'provider',
    });
  }

  async failClassified(
    jobId: string,
    workerId: string,
    error: string,
    traceGcsUri: string,
    failureKind: NonNullable<AssetGenJob['failureKind']>,
  ): Promise<void> {
    await this.terminate(jobId, workerId, 'failed', {
      variantCount: null,
      error,
      traceGcsUri,
      failureKind,
    });
  }

  private async terminate(
    jobId: string,
    workerId: string,
    status: 'succeeded' | 'failed',
    extra: {
      variantCount: number | null;
      error: string | null;
      traceGcsUri: string;
      failureKind?: AssetGenJob['failureKind'];
    },
  ): Promise<void> {
    const won = await this.store.guardedUpdate<AssetGenJob>(
      assetGenJobDoc(jobId),
      job => job !== null && job.status === 'running' && job.workerId === workerId,
      {
        status,
        stage: null,
        ...extra,
        finishedAt: this.store.serverTimestamp(),
      },
    );
    if (!won) throw new PlatformError('conflict', `${status}: asset gen job ${jobId} claim lost`);
  }

  /**
   * CANCEL a job at a member's request — the same semantics a cancelled BUILD
   * has (`cancelBuild`): the job goes TERMINAL with a reason naming who
   * cancelled it, rather than acquiring a fifth status value that every reader,
   * the reaper and the audit-job sibling would have to learn.
   *
   * A `queued` job dies before any provider call happens. A `running` job is
   * neutralized the way a reaped claim is: its next {@link heartbeat} sees a
   * non-running row, raises `conflict`, and the worker DROPS its result without
   * exposing a candidate — and the audio producer additionally re-asserts the
   * claim immediately before it would expose one, so a cancel landing mid-run
   * cannot publish a candidate the creator has stopped paying attention to.
   *
   * Quota is NOT refunded: the charge bought the attempt, which may already be
   * in flight at the provider. Same rule as a cancelled build.
   *
   * Returns false when the job is already terminal (an idempotent no-op) — the
   * CAS, not a pre-read, decides.
   */
  async cancel(jobId: string, reason: string): Promise<boolean> {
    if (!reason.trim()) throw new PlatformError('validation', 'cancel requires a reason');
    if (!(await this.get(jobId))) {
      throw new PlatformError('not-found', `cancel: asset gen job ${jobId} not found`);
    }
    return this.store.guardedUpdate<AssetGenJob>(
      assetGenJobDoc(jobId),
      (job) => job !== null && (job.status === 'awaiting-core' || job.status === 'queued' || job.status === 'running'),
      {
        status: 'failed',
        stage: null,
        variantCount: null,
        error: reason,
        failureKind: 'cancelled',
        finishedAt: this.store.serverTimestamp(),
      },
    );
  }

  /** Worker cleanup is not authority to cancel a replacement claim's shared
   * media. Wait for the logical composed parent to become terminal (or its
   * immutable deadline to expire), then check the child binding atomically.
   * A crash before terminal commit therefore leaves the child resumable. */
  async cancelForComposition(jobId: string, parentJobId: string, reason: string): Promise<boolean> {
    if (!reason.trim()) throw new PlatformError('validation', 'cancel requires a reason');
    if (!this.store.runTransaction) throw new PlatformError('unavailable', 'composition cancellation requires server transactions');
    return this.store.runTransaction(async tx => {
      const parent = await tx.get<BuildJob>(buildJobDoc(parentJobId));
      const run = parent?.measuredExecution;
      if (!parent || !run?.compositionDigest) return false;
      if (!['done','failed'].includes(parent.status)
        && this.now() < run.deadlineAt - run.limits.finishReserveMs) return false;
      return cancelBoundCompositionMediaChild(tx, { parentJobId, parent, jobId, reason,
        finishedAt: this.store.serverTimestamp() });
    });
  }

  /**
   * The OUTSTANDING (awaiting core, queued or running) job for a given (creation, name, kind),
   * or null. The kickoff dedupe: a second Generate for a name already being
   * generated reuses this job — no duplicate work, no second quota consume.
   */
  async findOutstanding(
    creationId: string,
    name: string,
    kind: CreationAssetKind,
  ): Promise<(AssetGenJob & { id: string }) | null> {
    const rows = await this.store.query<AssetGenJob>(assetGenJobsCollection(), {
      where: [
        ['creationId', '==', creationId],
        ['name', '==', name],
        ['kind', '==', kind],
      ],
    });
    const outstanding = rows
      .filter((j) => OUTSTANDING.includes(j.status))
      .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))[0];
    return outstanding ? { ...outstanding, id: outstanding.id } : null;
  }

  /**
   * Outstanding + recent jobs for one creation, newest first — the studio's
   * fetch-on-mount snapshot (survives navigation: the pending/completed jobs
   * are re-read authoritatively, not reconstructed from missed realtime events).
   */
  async listRecentForCreation(
    creationId: string,
    opts: { limit?: number } = {},
  ): Promise<Array<AssetGenJob & { id: string }>> {
    return this.store.query<AssetGenJob>(assetGenJobsCollection(), {
      where: [['creationId', '==', creationId]],
      orderBy: [['createdAt', 'desc']],
      ...(opts.limit !== undefined ? { limit: opts.limit } : {}),
    });
  }

  async listUnreconciledCompletions(limit = 25): Promise<Array<AssetGenJob & { id: string }>> {
    return this.store.query<AssetGenJob>(assetGenJobsCollection(), {
      where: [['completionRecoveryEligible', '==', true], ['status', 'in', ['succeeded', 'failed']], ['completion', '!=', null], ['completionReconciledAt', '==', null]],
      orderBy: [['completionCheckedAt', 'asc'], ['finishedAt', 'asc']], limit,
    });
  }

  /** Rotate attempted callbacks even when a build is busy, so a small stuck
   * cohort cannot starve later terminal waves. Failed dispatches remain retryable. */
  async markCompletionChecked(jobId: string): Promise<void> {
    await this.store.guardedUpdate<AssetGenJob>(assetGenJobDoc(jobId),
      job => job !== null && job.completionReconciledAt == null && (job.status === 'succeeded' || job.status === 'failed'),
      { completionCheckedAt: this.store.serverTimestamp() });
  }

  async markCompletionReconciled(jobIds: readonly string[]): Promise<void> {
    for (const id of jobIds) await this.store.guardedUpdate<AssetGenJob>(assetGenJobDoc(id),
      job => job !== null && (job.status === 'succeeded' || job.status === 'failed'),
      { completionReconciledAt: this.store.serverTimestamp() });
  }

  /** Read the entire origin wave; recent windows can hide running siblings. */
  async listCompletionWave(creationId: string, originVersion: number): Promise<Array<AssetGenJob & { id: string }>> {
    return (await this.listRecentForCreation(creationId)).filter(job => job.completion?.originVersion === originVersion);
  }

  /**
   * List RUNNING jobs whose heartbeat is older than `staleMs` — the reaper's
   * requeue candidates (a worker died mid-run). Uses the injected clock.
   */
  async listStale(staleMs: number): Promise<Array<AssetGenJob & { id: string }>> {
    const cutoff = this.now() - staleMs;
    const running = await this.store.query<AssetGenJob>(assetGenJobsCollection(), {
      where: [['status', '==', 'running']],
    });
    return running.filter((j) => (j.heartbeatAt ?? 0) < cutoff);
  }

  /**
   * List QUEUED jobs enqueued more than `staleMs` ago and never claimed — the
   * starvation candidates {@link listStale} cannot see (no worker, no
   * heartbeat). Each queued job is claimable, so the reaper re-pushes a fresh
   * Cloud Task for it (the claim CAS keeps a re-push from double-generating).
   */
  async listStuckQueued(staleMs: number): Promise<Array<AssetGenJob & { id: string }>> {
    const cutoff = this.now() - staleMs;
    const queued = await this.store.query<AssetGenJob>(assetGenJobsCollection(), {
      where: [['status', '==', 'queued']],
    });
    return queued.filter((j) => (j.createdAt ?? 0) < cutoff);
  }

  /** Newly queued platform title treatments. Unlike creator kickoffs, these
   * rows originate in a DB trigger and therefore need the scheduler to mint
   * their first Cloud Task; claim CAS makes repeat dispatches harmless. */
  async listQueuedTitleOverlays(): Promise<Array<AssetGenJob & { id: string }>> {
    const queued = await this.store.query<AssetGenJob>(assetGenJobsCollection(), {
      where: [['status', '==', 'queued']],
    });
    return queued.filter((job) => job.titleOverlay != null);
  }

  /**
   * REQUEUE a stale job back to `queued` — clears the claim and BUMPS `attempts`
   * so the reaper requeues a given job at most once before abandoning it. No
   * owner guard (the reaper is a system actor). Returns the requeued job.
   */
  async requeue(jobId: string, observed?: AssetGenJob): Promise<AssetGenJob> {
    const job = observed ?? await this.get(jobId);
    if (!job) throw new PlatformError('not-found', `requeue: asset gen job ${jobId} not found`);
    if (job.kind === 'music') {
      throw new PlatformError(
        'validation',
        'paid music jobs cannot be requeued; settle to a supported default and require a newly capped request',
        { retryable: false },
      );
    }
    const won = await this.store.guardedUpdate<AssetGenJob>(assetGenJobDoc(jobId),
      current => current !== null && current.status === job.status && (current.status === 'running' || current.status === 'queued') && current.workerId === job.workerId
        && current.heartbeatAt === job.heartbeatAt && current.attempts === job.attempts,
      { status: 'queued', stage: null, workerId: null, heartbeatAt: null, finishedAt: null,
        error: null, failureKind: null, attempts: (job.attempts ?? 0) + 1 });
    if (!won) throw new PlatformError('conflict', 'asset claim changed before recovery');
    return (await this.get(jobId))!;
  }

  /** Reap one measured child while atomically preserving unknown provider
   * exposure on its parent ledger. Returns null for a legacy job. */
  async recoverStaleMeasured(
    jobId: string,
    observed: AssetGenJob,
    reason = 'measured media worker became unresponsive',
  ): Promise<'requeued' | 'abandoned' | null> {
    const observedBuildJobId = observed.measuredBudget?.buildJobId;
    if (!observedBuildJobId) return null;
    if (!this.store.runTransaction) throw new PlatformError('unavailable', 'measured media recovery requires server transactions');
    return this.store.runTransaction(async tx => {
      const parent = await tx.get<BuildJob>(buildJobDoc(observedBuildJobId));
      const job = await tx.get<AssetGenJob>(assetGenJobDoc(jobId));
      const run = parent?.measuredExecution;
      if (!parent || !job || !run || job.measuredBudget?.buildJobId !== observedBuildJobId) {
        throw new PlatformError('conflict', 'measured media recovery authority changed');
      }
      if (job.status !== observed.status || job.workerId !== observed.workerId
        || job.heartbeatAt !== observed.heartbeatAt || job.attempts !== observed.attempts) {
        throw new PlatformError('conflict', 'measured media claim changed before recovery');
      }
      const reservations = { ...run.reservations };
      let ledgerChanged = false;
      for (const [id, reservation] of Object.entries(reservations)) {
        if (reservation.mediaJobId === jobId
          && reservation.mediaClaimAttempt === (job.attempts ?? 0)
          && reservation.status === 'reserved') {
          reservations[id] = {
            ...reservation,
            status: 'uncertain',
            error: reason,
            settledAt: this.now(),
          };
          ledgerChanged = true;
        }
      }
      if (ledgerChanged) {
        await tx.update(buildJobDoc(observedBuildJobId), {
          measuredExecution: {
            ...run,
            ledgerRevision: (run.ledgerRevision ?? 0) + 1,
            reservations,
          },
        });
      }
      const canRetry = job.kind !== 'music'
        && (job.attempts ?? 0) < 1
        && parent.status !== 'done'
        && parent.status !== 'failed'
        && this.now() < run.deadlineAt - run.limits.finishReserveMs;
      if (canRetry) {
        await tx.update(assetGenJobDoc(jobId), {
          status: 'queued', stage: null, workerId: null, heartbeatAt: null,
          finishedAt: null, error: null, failureKind: null, attempts: (job.attempts ?? 0) + 1,
        });
        return 'requeued';
      }
      await tx.update(assetGenJobDoc(jobId), {
        status: 'failed', stage: null, error: reason,
        failureKind: 'timeout', finishedAt: this.store.serverTimestamp(),
      });
      return 'abandoned';
    });
  }

  /**
   * ABANDON a job — force it to `failed` from ANY non-terminal state with a
   * reason, no owner guard (unlike {@link fail}). The reaper uses this to
   * terminate a job whose worker is gone and which has exhausted its requeues.
   * A no-op if the job is already terminal.
   */
  async abandon(jobId: string, reason: string, observed?: AssetGenJob): Promise<void> {
    const job = observed ?? await this.get(jobId);
    if (!job) throw new PlatformError('not-found', `abandon: asset gen job ${jobId} not found`);
    if (job.status === 'succeeded' || job.status === 'failed') return;
    const won = await this.store.guardedUpdate<AssetGenJob>(assetGenJobDoc(jobId),
      current => current !== null && current.status === job.status && current.workerId === job.workerId
        && current.heartbeatAt === job.heartbeatAt && current.attempts === job.attempts,
      {
        status: 'failed',
        stage: null,
        error: reason,
        failureKind: /timed?\s*out|unresponsive/i.test(reason) ? 'timeout' : 'worker',
        finishedAt: this.store.serverTimestamp(),
      });
    if (!won) throw new PlatformError('conflict', 'asset claim changed before abandonment');
  }
}

/**
 * Cutoff (ms since last heartbeat) after which a RUNNING asset gen job counts
 * as STALE — its worker is presumed dead and the claim is safe to requeue. The
 * builder's heartbeat cadence (`ASSET_GEN_HEARTBEAT_MS` in
 * `services/builder/src/worker/assetGenWorker.ts`) sits far under this, so a
 * live worker never looks stale. Sized for a multi-minute generation (a run
 * can wait out one Gemini retry per image ≈ 250s) plus slack.
 */
export const ASSET_GEN_STALE_MS = 10 * 60 * 1000;
